import { useState, useCallback, useEffect, useRef } from "react";

type Envelope<T> = { version: string; value: T };

/**
 * Like useState but persists to sessionStorage.
 * State survives navigation but clears when the tab closes.
 *
 * Optional `version` parameter : when it changes (e.g. `bien.updated_at` from DB
 * changes because another source modified the record), the stored value is
 * discarded and the form reinitialises from `initialValue`. Without this, stale
 * session values could be auto-saved back to DB and overwrite fresh data.
 */
export function useSessionState<T>(
  key: string,
  initialValue: T,
  version: string = "default",
) {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored) as Envelope<T> | T;
        // Legacy format (no envelope) or outdated version → reset
        const isEnvelope =
          typeof parsed === "object" &&
          parsed !== null &&
          "version" in parsed &&
          "value" in parsed;
        if (!isEnvelope) {
          const env: Envelope<T> = { version, value: initialValue };
          sessionStorage.setItem(key, JSON.stringify(env));
          return initialValue;
        }
        const env = parsed as Envelope<T>;
        if (env.version !== version) {
          // DB record has been updated since last session save → discard stale cache
          const fresh: Envelope<T> = { version, value: initialValue };
          sessionStorage.setItem(key, JSON.stringify(fresh));
          return initialValue;
        }
        // Same version : shallow-merge to absorb new keys from initialValue
        if (
          typeof initialValue === "object" &&
          initialValue !== null &&
          !Array.isArray(initialValue)
        ) {
          const merged = { ...initialValue, ...(env.value as object) } as T;
          sessionStorage.setItem(key, JSON.stringify({ version, value: merged }));
          return merged;
        }
        return env.value;
      }
      const env: Envelope<T> = { version, value: initialValue };
      sessionStorage.setItem(key, JSON.stringify(env));
      return initialValue;
    } catch {
      return initialValue;
    }
  });

  // If the `version` changes mid-lifecycle (ex: bien.updated_at refreshed via
  // React Query), reset state from initialValue and flush sessionStorage.
  const lastVersionRef = useRef(version);
  useEffect(() => {
    if (lastVersionRef.current === version) return;
    lastVersionRef.current = version;
    setState(initialValue);
    try {
      sessionStorage.setItem(key, JSON.stringify({ version, value: initialValue }));
    } catch {
      /* noop */
    }
    // initialValue intentionnellement omis : ne rerun QUE quand la version change,
    // pas à chaque re-render où initialValue serait re-construit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, key]);

  const setSessionState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next = typeof value === "function" ? (value as (prev: T) => T)(prev) : value;
        try {
          sessionStorage.setItem(key, JSON.stringify({ version, value: next }));
        } catch {
          // sessionStorage full or unavailable
        }
        return next;
      });
    },
    [key, version],
  );

  return [state, setSessionState] as const;
}
